import { useState, useEffect, useRef } from "react";
import styles from "./IframeMap.module.css";

type ComponentState = "loading" | "loaded" | "error";

type IframeMapProps = {
  src: string;
  title: string;
};

const IframeMap = ({ src, title }: IframeMapProps) => {
  const [componentState, setComponentState] =
    useState<ComponentState>("loading");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLoadTimeout = () => {
    if (!timeoutRef.current) {
      return;
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  };

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setComponentState("error");
      timeoutRef.current = null;
    }, 10000);

    return () => {
      clearLoadTimeout();
    };
  }, []);

  const handleLoad = () => {
    clearLoadTimeout();
    setComponentState("loaded");
  };

  const handleIframeError = () => {
    clearLoadTimeout();
    setComponentState("error");
  };

  if (componentState === "error") {
    throw new Error(
      "Failed to load the map. Please check your connection and try again.",
    );
  }

  return (
    <>
      {componentState === "loading" && (
        <span className={styles.loadingText}>Loading...</span>
      )}
      <div className={styles.frame}>
        <iframe
          className={`${styles.iframe} ${componentState === "loading" ? styles.iframeHidden : ""}`}
          onLoad={handleLoad}
          onError={handleIframeError}
          src={src}
          title={title}
        />
      </div>
    </>
  );
};

export default IframeMap;
